import { AlertTriangle, CheckCircle2, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TaxonomyListImpact } from '@/entities/taxonomy-list';

export interface ListImpactSummaryProps {
  impact: TaxonomyListImpact | undefined;
  isLoading?: boolean;
}

/**
 * Blast radius of publishing this list version.
 *
 * Shown INSIDE the publish confirmation, not after the fact: a vocabulary edit
 * silently changing what a dozen schemas mean is the failure this exists to
 * prevent. Claravine only warns like this on delete, which is the wrong moment.
 *
 * Pinned schemas are called out separately because they are the reassuring
 * half — they froze this list at an older version and cannot be affected.
 */
export function ListImpactSummary({ impact, isLoading }: ListImpactSummaryProps) {
  const { t } = useTranslation('knowledge');
  if (isLoading) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('taxonomy.listImpactSummary.checkingWhichSchemasUseList')}
      </p>
    );
  }
  if (!impact || impact.schemas.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <CheckCircle2 className="size-3.5 shrink-0" aria-hidden />
        {t('taxonomy.listImpactSummary.noSchemaBindsFieldList')}
      </p>
    );
  }

  const following = impact.schemas.filter((s) => s.follows);
  const pinned = impact.schemas.filter((s) => !s.follows);

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border-light bg-surface p-3">
      {following.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
            <AlertTriangle className="size-3.5 shrink-0 text-warning" aria-hidden />
            {t('taxonomy.listImpactSummary.schemasFollowList', { count: following.length })}
          </span>
          <ul className="ml-5 flex flex-col gap-0.5">
            {following.map((s) => (
              <li key={s.taxonomyId} className="truncate text-xs text-muted-foreground">
                {s.taxonomyName}
                <span className="ml-1 opacity-70">({s.fieldNames.join(', ')})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pinned.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Lock className="size-3.5 shrink-0" aria-hidden />
            {t('taxonomy.listImpactSummary.pinnedToOlderVersion', { count: pinned.length })}
          </span>
          <ul className="ml-5 flex flex-col gap-0.5">
            {pinned.map((s) => (
              <li key={s.taxonomyId} className="truncate text-xs text-muted-foreground">
                {s.taxonomyName}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

ListImpactSummary.displayName = 'ListImpactSummary';
